/* global Wix */
import { loadIntlPolyfillData } from './load-locale-data';
import { cleanLocale, DEFAULT_LOCALE } from './config';
import { logWidgetSystemBI } from 'shared/bi/bi-widget-system';
import trackError from 'shared/utils/track-error';
import { hydratedData } from 'services/hydrated-data';

function getAppLocale() {
  let locale;

  if (hydratedData.getStyleParams()) {
    locale = hydratedData.getLocale() || Wix.Utils.getLocale();
  }

  return new Promise(resolve => {
    if (locale && cleanLocale(locale) === locale) {
      resolve(locale);
    } else {
      logWidgetSystemBI('locale.appSettingsLoadStart');

      Wix.Styles.getStyleParams(appSettings => {
        logWidgetSystemBI('locale.appSettingsLoaded');
        resolve(_.get(appSettings, 'fonts.language') || Wix.Utils.getLocale());
      });
    }
  });
}

function getUserLocale() {
  return new Promise(resolve => {
    resolve(Wix.Utils.getLocale());
  });
}

export class LocaleService {
  locale = null;
  translations = {};
  reactIntlData = [];

  _isReady = false;
  _handlers = [];

  constructor({ loadLocaleData }) {
    if (!loadLocaleData) {
      throw new Error('loadLocaleData is required parameter');
    }
    this.loadLocaleData = loadLocaleData;
  }

  initAppLocale() {
    return getAppLocale()
      .catch(() => getUserLocale())
      .then(locale => this._load(locale));
  }

  initUserLocale() {
    return getUserLocale()
      .catch(() => DEFAULT_LOCALE)
      .then(locale => this._load(locale));
  }

  _load(locale) {
    locale = cleanLocale(locale);

    const localeDataPromise = this.loadLocaleData(locale).catch(() => ({
      locale: DEFAULT_LOCALE,
      translations: {},
      reactIntlData: [],
    }));
    const polyfillPromise = loadIntlPolyfillData(locale).catch(error => {
      trackError(error);
      logWidgetSystemBI('locale.error.polyfillNotLoaded');
    });

    return Promise.all([localeDataPromise, polyfillPromise]).then(
      ([localeData]) => {
        _.assign(this, localeData);
        this._isReady = true;
        this._handlers.forEach(handler => this._callOnLoadHandler(handler));
        return Promise.resolve(this);
      },
    );
  }

  onLoad(handler) {
    this._handlers.push(handler);

    if (this._isReady) {
      this._callOnLoadHandler(handler);
    }
  }

  _callOnLoadHandler(handler) {
    try {
      handler({
        locale: this.locale,
        translations: this.translations,
        reactIntlData: this.reactIntlData,
      });
    } catch (e) {
      trackError(e);
    }
  }
}
